import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button } from "../../../../components/Button";
import { LoadingModal } from "../../../../components/LoadingModal";
import { PlusTag } from "../../../../components/PlusTag";
import constants from "../../../../constants";
import { useAppState } from "../../../../contexts/App";
import { useQueryUrl } from "../../../../hooks/QueryUrl";
import { Site } from "../../../../model/site";
import {
  keepoInternalAnalyticsService,
  purchaseFromSubscription,
  waitForPurchase,
} from "../../../../services/analytics";
import { api, ApiSiteResponse, Subscription, SubscriptionDetail } from "../../../../services/api";
import { formatDate as formatLocaleDate } from "../../../../services/i18ln/format";
import { formatMoney } from "../../../../utils/money";
import { JustPaid } from "../../../Editor/pages/Publish/pages/Ready";
import { Confetti } from "./Confetti";
import { Actions, Column, Container, Hero, Note, Receipt, Unlocked } from "./styles";

// What subscribing just unlocked: four of the PLUS screen's benefits
const UNLOCKED = [
  { key: "domain", icon: "🌐" },
  { key: "brand", icon: "✨" },
  { key: "sections", icon: "🧩" },
  { key: "analytics", icon: "📊" },
];

const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={3}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true">
    <path d="m5 12.5 4.5 4.5L19 7.5" />
  </svg>
);

type Props = {
  setSite: (site: Site) => void;
};

/** A PLUS Keepo with nothing published yet: the one a subscription without a Keepo just created. */
const isNewPlusKeepo = (site: ApiSiteResponse) => !site.site && site.subscription?.type === "PLUS";

const wasNotified = (key: string) => {
  try {
    return !!sessionStorage.getItem(key);
  } catch {
    // Storage unavailable (private mode): notifies as before
    return false;
  }
};

const markNotified = (key: string) => {
  try {
    sessionStorage.setItem(key, "1");
  } catch {
    // Storage unavailable
  }
};

export const SuccessPage = ({ setSite }: Props) => {
  const { t, i18n } = useTranslation();
  const query = useQueryUrl();
  const navigate = useNavigate();

  const { appState, setAppState } = useAppState();

  const [loading, setLoading] = useState(true);
  const [subscribedSite, setSubscribedSite] = useState<ApiSiteResponse>();
  // The subscription, for the receipt (shown without it if it can't be read)
  const [detail, setDetail] = useState<SubscriptionDetail>();
  const subscribedSiteId = query.get("siteId");
  const editorFlow = query.get("flow");
  const isEditorFlow = editorFlow === "create" || editorFlow === "edit";
  const isBoleto = query.get("method") === "boleto";

  const editHandler = useCallback(() => {
    setSite((subscribedSite?.draft || subscribedSite?.site) as Site);

    setAppState({
      ...appState,
      editingSiteId: subscribedSite?.siteId as string,
      editingSiteModified: false,
      published: !!subscribedSite?.site,
      editingSiteDisabled: false,
      editingSiteSubscription: subscribedSite?.subscription as Subscription,
    });

    navigate(subscribedSite?.site ? "/editor/pages" : "/editor/title");
  }, [
    appState,
    navigate,
    setAppState,
    setSite,
    subscribedSite?.draft,
    subscribedSite?.site,
    subscribedSite?.siteId,
    subscribedSite?.subscription,
  ]);

  const nextPageHandler = useCallback(() => {
    if (subscribedSite?.disabled) {
      navigate("/sites");
      return;
    } else {
      editHandler();
    }
  }, [editHandler, navigate, subscribedSite?.disabled]);

  // Subscribed while creating/editing a Keepo: back to its last step, which publishes it
  const finishEditorFlow = useCallback(
    (site: ApiSiteResponse) => {
      setSite((site.draft || site.site) as Site);
      setAppState({
        ...appState,
        editingSiteId: site.siteId,
        editingSiteModified: false,
        published: !!site.site,
        editingSiteDisabled: site.disabled,
        editingSiteSubscription: site.subscription as Subscription,
      });
      // Publishing opens saying the payment went through (or, for a boleto, that it was generated)
      const justPaid: JustPaid = isBoleto ? "boleto" : "paid";
      navigate("/editor/publish/ready", { replace: true, state: { justPaid } });
    },
    [appState, isBoleto, navigate, setAppState, setSite]
  );

  const load = useCallback(async () => {
    api.clearCacheSites();
    const sites = await api.getSites();

    const predicate: (site: ApiSiteResponse) => boolean = subscribedSiteId
      ? site => site.siteId === subscribedSiteId && site.subscription?.type === "PLUS"
      : isNewPlusKeepo;

    const subscribedSite = sites.find(predicate);

    if (!subscribedSite) {
      navigate(`/`, { replace: true });
      navigate(`/sites`);
      return;
    }

    if (isEditorFlow) {
      finishEditorFlow(subscribedSite);
      return;
    }

    setSubscribedSite(subscribedSite);
    setDetail(await api.getSubscriptionDetail(subscribedSite.siteId).catch(() => undefined));
    setLoading(false);
  }, [finishEditorFlow, isEditorFlow, navigate, subscribedSiteId]);

  // The "purchase" event, once per Keepo (reloading this screen isn't another purchase) and only when the
  // payment is known: confirmed by Stripe, or a boleto still to be paid ("purchase_pending")
  const notifyPurchase = async () => {
    try {
      const siteId = subscribedSiteId ?? (await api.getSites()).find(isNewPlusKeepo)?.siteId;
      if (!siteId) {
        return;
      }

      const key = `keepo.purchaseNotified.${siteId}`;
      if (wasNotified(key)) {
        return;
      }

      const user = await api.getUser();
      const purchased = await waitForPurchase(siteId);
      if (!purchased) {
        return;
      }

      // Another arrival may have reported it while this one waited
      if (wasNotified(key)) {
        return;
      }
      markNotified(key);

      const purchase = purchaseFromSubscription(purchased);
      if (purchased.awaitingPayment) {
        keepoInternalAnalyticsService.notifyPurchasePendingEvent(user, purchase);
      } else {
        keepoInternalAnalyticsService.notifyPurchaseEvent(user, purchase);
      }
    } catch {
      // Analytics never gets in the way of the page; nothing was marked, so the next visit tries again
    }
  };

  // `load` is recreated whenever `finishEditorFlow`'s `appState` dependency changes -
  // which happens inside `finishEditorFlow` itself, right before it navigates away.
  // Guard against that identity change re-triggering this effect and calling load()
  // (and its api.publish) a second time before the navigation unmounts the page.
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (hasLoadedRef.current) {
      return;
    }
    hasLoadedRef.current = true;
    load();
  }, [load]);

  // Once, on arrival (the Keepo in the address doesn't change while here)
  useEffect(() => {
    notifyPurchase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <LoadingModal />;
  }

  const prefix = "pages.subscription.success";
  const format = (date?: string) => (date ? formatLocaleDate(date, i18n.language) : "");
  const keepo = subscribedSite?.site || subscribedSite?.draft;
  const address = keepo?.url ? `${keepo.url}.${constants.nameSite}` : undefined;
  const isOneTime = !!detail?.paymentType;
  const lastCard = detail?.invoices?.find(invoice => invoice.card)?.card;
  const amount = detail?.plan?.amount !== undefined ? formatMoney(detail.plan.amount, "brl", i18n.language) : undefined;
  const method = detail?.paymentType
    ? t(`pages.subscription.checkout.methods.${detail.paymentType}.title`)
    : lastCard
      ? t(`${prefix}.receipt.card`, { last4: lastCard.last4 })
      : undefined;

  return (
    <Container>
      <Column>
        <Hero>
          <span className="check">
            <CheckIcon />
          </span>
          <PlusTag>✦ Keepo Plus</PlusTag>
          <h1>{t(`${prefix}.title`)}</h1>
          <p>{t(`${prefix}.message.description`)}</p>
        </Hero>

        {detail && (
          <Receipt aria-label={t(`${prefix}.receipt.label`)}>
            <div>
              <dt>{t(`${prefix}.receipt.keepo`)}</dt>
              <dd>{keepo?.title || address || t(`${prefix}.receipt.newKeepo`)}</dd>
            </div>
            <div>
              <dt>{t(`${prefix}.receipt.plan`)}</dt>
              <dd>
                {t(
                  `pages.subscription.features.plans.${detail.plan?.interval === "year" ? "annually" : "monthly"}.title`
                )}
              </dd>
            </div>
            {method && (
              <div>
                <dt>{t(`${prefix}.receipt.method`)}</dt>
                <dd>{method}</dd>
              </div>
            )}
            <div>
              <dt>{t(`${prefix}.receipt.${isOneTime ? "validUntil" : "nextCharge"}`)}</dt>
              <dd>{format(detail.currentPeriodEnd)}</dd>
            </div>
            {amount && (
              <div className="total">
                <dt>{t(`${prefix}.receipt.amount`)}</dt>
                <dd>{amount}</dd>
              </div>
            )}
          </Receipt>
        )}

        {detail?.awaitingPayment && detail.paymentType === "boleto" && (
          <Note role="status">
            <b>{t(`${prefix}.boleto.title`)}</b> {t(`${prefix}.boleto.description`)}
          </Note>
        )}

        <Unlocked>
          <h2>{t(`${prefix}.unlocked`)}</h2>
          <ul>
            {UNLOCKED.map(({ key, icon }) => (
              <li key={key}>
                <span className="icon" aria-hidden="true">
                  {icon}
                </span>
                <b>{t(`pages.subscription.features.page.benefits.${key}.title`)}</b>
                <span className="text">{t(`pages.subscription.features.page.benefits.${key}.text`)}</span>
              </li>
            ))}
          </ul>
        </Unlocked>
      </Column>

      <Actions>
        <div className="inner">
          <Button type="INFO" size="LG" block onClick={nextPageHandler}>
            {!subscribedSite?.site
              ? t("pages.subscription.success.createNewKeepoContinueButton")
              : t("pages.subscription.success.editKeepoContinueButton")}{" "}
            →
          </Button>
          <div className="links">
            {subscribedSite && (
              <button
                type="button"
                onClick={() => navigate(`/subscription/detail?site=${encodeURIComponent(subscribedSite.siteId)}`)}>
                {t(`${prefix}.viewSubscription`)}
              </button>
            )}
            <button type="button" onClick={() => navigate("/sites")}>
              {t(`${prefix}.goToKeepos`)}
            </button>
          </div>
        </div>
      </Actions>
      <Confetti />
    </Container>
  );
};
